import React, { useEffect, useMemo, useState } from 'react';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import {
  SalvButton,
  SalvDropdown,
  SalvFieldHeader,
  SalvModal,
  SalvRadio,
  SalvRadioGroup,
  SalvTextField,
  Size,
} from '@salvapp/ui-components';
import { scenarioLibraryInternalApi } from '../apis';
import { convertToDropdownOption } from '../common/helper';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import { InternalApiLibraryScenario, InternalApiScenarioTypeV2 } from '../api-internal';

import * as Yup from 'yup';
import { Link } from 'react-router';
import { capitalize } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import './NewScenarioModal.scss';

export interface ImportScenarioFormValues {
  name: string;
  category: DropdownOption | undefined;
  typology: DropdownOption | undefined;
  type: InternalApiScenarioTypeV2 | undefined;
}

interface ImportLibraryScenarioModalProps {
  showModal: boolean;
  onClose: () => void;
  onCreate: (scenarioId: string) => void;
  libraryScenario: InternalApiLibraryScenario;
}

const ImportLibraryScenarioModal = ({
  showModal,
  onClose,
  onCreate,
  libraryScenario,
}: ImportLibraryScenarioModalProps) => {
  const [selectedCategory, setSelectedCategory] = useState<DropdownOption | undefined>(
    libraryScenario.categories.length === 1
      ? convertToDropdownOption(libraryScenario.categories[0].name, libraryScenario.categories[0].id)
      : undefined
  );
  const submit = async (
    values: ImportScenarioFormValues,
    { setSubmitting, resetForm }: FormikHelpers<ImportScenarioFormValues>
  ) => {
    try {
      const scenario = await scenarioLibraryInternalApi.importLibraryScenario({
        name: values.name,
        scenarioHandle: libraryScenario.id,
        categoryHandle: values.category!.value,
        type: values.type!,
        typologyHandle: values.typology?.value,
      });
      onCreate(scenario.id!);
      onClose();
      resetForm();
      setSelectedCategory(undefined);
    } catch (_) {
      // do nothing, error pops up
    } finally {
      setSubmitting(false);
    }
  };

  const typologies = useMemo(() => {
    if (selectedCategory) {
      return libraryScenario.categories.find((category) => category.id === selectedCategory.value)?.typologies ?? [];
    }
    return [];
  }, [selectedCategory, libraryScenario.categories]);

  const getDefaultTypology = () =>
    typologies.length === 1 ? convertToDropdownOption(typologies[0].name, typologies[0].id) : undefined;

  const initialValues: ImportScenarioFormValues = {
    name: libraryScenario.name,
    category: selectedCategory,
    typology: typologies.length === 1 ? convertToDropdownOption(typologies[0].name, typologies[0].id) : undefined,
    type: libraryScenario.type.length === 1 ? libraryScenario.type[0] : undefined,
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    category: Yup.object().required('Category is required'),
    typology: Yup.object().when([], {
      is: () => !!typologies.length,
      then: (schema) => schema.required('Typology is required'),
      otherwise: (schema) => schema,
    }),
    type: Yup.string().required('Type is required'),
  });

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, formikHelpers) => {
        submit(values, formikHelpers);
      }}
      validationSchema={validationSchema}
      validateOnChange={true}
      validateOnBlur={false}
    >
      {({ values, setFieldValue, isSubmitting, errors, resetForm, touched }: FormikProps<ImportScenarioFormValues>) => {
        useEffect(() => {
          setFieldValue('typology', getDefaultTypology());
        }, [typologies]);

        return (
          <SalvModal
            show={showModal}
            dialogClassName='new-scenario-modal'
            onHide={() => {
              onClose();
              resetForm();
              setSelectedCategory(undefined);
            }}
          >
            <Form>
              <SalvModal.Header closeButton>Import scenario</SalvModal.Header>

              <SalvModal.Body>
                <div className='text-m' style={{ color: 'var(--text-medium)' }}>
                  This scenario will be added in an inactive status. You can modify and test it before activating it.
                </div>
                <SalvTextField
                  label='Scenario name'
                  required={true}
                  isInvalid={!!errors.name}
                  onChange={(value: string) => setFieldValue('name', value)}
                  value={values.name}
                  disabled={isSubmitting}
                />
                <div className='d-flex'>
                  <SalvDropdown
                    label='Category'
                    required={true}
                    isInvalid={touched.category && !!errors.category}
                    placeholder='Select category'
                    options={libraryScenario.categories.map((category) =>
                      convertToDropdownOption(category.name, category.id)
                    )}
                    size={Size.MEDIUM}
                    onChange={(options) => {
                      if (options[0]) {
                        setFieldValue('category', options[0]);
                        setSelectedCategory(options[0]);
                      }
                    }}
                    value={values.category}
                    disabled={isSubmitting}
                    clearable={false}
                  />

                  {!!typologies.length && (
                    <SalvDropdown
                      label='Typology'
                      required={true}
                      isInvalid={touched.typology && !!errors.typology}
                      placeholder='Select typology'
                      options={typologies.map((typology) => convertToDropdownOption(typology.name, typology.id))}
                      size={Size.MEDIUM}
                      onChange={(options) => {
                        if (options[0]) {
                          setFieldValue('typology', options[0]);
                        }
                      }}
                      value={values.typology}
                      disabled={isSubmitting}
                      className='m-l-16'
                      clearable={false}
                    />
                  )}
                </div>

                <div className='border-bottom'></div>

                <SalvRadioGroup
                  label='Scenario type'
                  description={
                    <div>
                      <div>
                        For more information on scenario types,{' '}
                        <Link to='https://help.salv.com/en/articles/149651-scenario-types' target='_blank'>
                          read here.
                        </Link>
                      </div>
                      <div>Scenario type cannot be changed after the scenario is created.</div>
                    </div>
                  }
                  required={true}
                  size={Size.MEDIUM}
                  onChange={(type) => {
                    setFieldValue('type', type as InternalApiScenarioTypeV2);
                  }}
                  value={values.type}
                  disabled={isSubmitting}
                >
                  <div className='d-flex'>
                    {libraryScenario.type.map((type) => (
                      <SalvRadio
                        key={type}
                        value={type}
                        label={capitalize(type.split('_').join('-'))}
                        invalid={touched.type && !!errors.type}
                      />
                    ))}
                  </div>
                </SalvRadioGroup>

                {libraryScenario.schemaRestrictions && (
                  <div>
                    <div className='border-bottom m-b-24'></div>

                    <div className='d-flex align-items-center'>
                      <SalvFieldHeader
                        className='m-b-8'
                        size={Size.MEDIUM}
                        label='Prerequisites'
                        description='The following prerequisites need to be met for this scenario.'
                      />
                    </div>

                    <div>
                      <FontAwesomeIcon
                        icon={faCircleExclamation}
                        className='fe m-r-8'
                        style={{ color: 'var(--text-medium)' }}
                      />
                      {libraryScenario.schemaRestrictions}
                    </div>
                  </div>
                )}
              </SalvModal.Body>

              <SalvModal.Footer>
                <SalvButton
                  variant='muted'
                  disabled={false}
                  size={Size.LARGE}
                  onClick={() => {
                    onClose();
                    resetForm();
                    setSelectedCategory(undefined);
                  }}
                  label='Cancel'
                />

                <SalvButton
                  type='submit'
                  variant='primary'
                  disabled={isSubmitting}
                  size={Size.LARGE}
                  label='Import scenario'
                />
              </SalvModal.Footer>
            </Form>
          </SalvModal>
        );
      }}
    </Formik>
  );
};

export default ImportLibraryScenarioModal;
